<template>
  <div class="ai-calling-settings" :class="{ 'ai-calling-settings--embedded': embedded }">
    <b-alert v-if="!canManage" variant="warning" show class="mb-0">
      <div class="d-flex align-items-start">
        <feather-icon icon="AlertTriangleIcon" size="18" class="mr-75 flex-shrink-0 mt-25" />
        <div>
          <strong class="d-block mb-25">Permission required</strong>
          <span class="small">You do not have permission to manage AI Calling settings. Contact an administrator if you need access.</span>
        </div>
      </div>
    </b-alert>

    <div v-else>
      <div class="d-flex flex-wrap justify-content-between align-items-start mb-1">
        <div class="ai-calling-settings__intro mr-1 mb-50">
          <h5 class="ai-calling-settings__title mb-25">AI Calling</h5>
          <p class="text-muted small mb-50">
            Phones candidates see, and who handles Screening, Drive, and Other.
          </p>
          <small class="text-muted d-block mb-25">
            Step {{ activeTab + 1 }} of {{ wizardSteps.length }} — {{ wizardSteps[activeTab] }}
          </small>
          <b-progress
            :value="activeTab + 1"
            :max="wizardSteps.length"
            height="4px"
            class="ai-calling-progress"
          />
        </div>
        <div class="d-flex flex-wrap align-items-center">
          <b-button
            size="sm"
            variant="outline-secondary"
            class="mr-50"
            :disabled="loading"
            title="Reload"
            aria-label="Reload"
            @click="loadConfig"
          >
            <b-spinner small v-if="loading" />
            <feather-icon v-else icon="RefreshCwIcon" size="14" />
          </b-button>
          <b-button
            variant="primary"
            :disabled="saving || loading"
            @click="saveConfig"
          >
            <b-spinner small v-if="saving" class="mr-25" />
            <feather-icon v-else icon="SaveIcon" size="14" class="mr-25" />
            Save settings
          </b-button>
        </div>
      </div>

      <b-alert v-if="loadError" variant="warning" show class="mb-1 py-75">
        <div class="d-flex align-items-start">
          <feather-icon icon="AlertTriangleIcon" size="16" class="mr-50 flex-shrink-0 mt-25" />
          <span class="small">{{ loadError }}</span>
        </div>
      </b-alert>

      <b-overlay :show="loading" rounded spinner-small>
      <b-form @submit.prevent="saveConfig">
        <b-tabs
          v-model="activeTab"
          pills
          nav-class="ai-wizard-tabs d-flex flex-wrap"
          content-class="mt-75"
        >
          <!-- 1. Setup -->
          <b-tab :title="wizardSteps[0]">
            <b-alert show class="ai-calling-note py-75 mb-1">
              <div class="d-flex align-items-start">
                <feather-icon icon="InfoIcon" size="16" class="ai-calling-brand-icon mr-50 flex-shrink-0 mt-25" />
                <div class="small">
                  Create agents on the
                  <b-button
                    v-if="embedded"
                    variant="link"
                    class="p-0 align-baseline ai-calling-link"
                    @click="goToAgentsTab"
                  >
                    Agents
                  </b-button>
                  <strong v-else>Agents</strong>
                  tab, pick who handles Screening / Drive / Other, then Save.
                  Open a job → AI Calling to test.
                </div>
              </div>
            </b-alert>

            <b-row>
              <b-col cols="12" md="6" class="mb-1">
                <b-card class="ai-calling-section h-100 mb-0" body-class="p-1">
                  <div class="d-flex align-items-start justify-content-between">
                    <div class="mr-1">
                      <div class="ai-calling-section__title">Turn on AI Calling</div>
                      <p class="text-muted small mb-0">When off, no one can place AI calls.</p>
                    </div>
                    <b-form-checkbox v-model="form.enabled" switch class="mb-0 mt-25">
                      {{ form.enabled ? 'On' : 'Off' }}
                    </b-form-checkbox>
                  </div>
                </b-card>
              </b-col>
              <b-col cols="12" md="6" class="mb-1">
                <b-card class="ai-calling-section h-100 mb-0" body-class="p-1">
                  <b-form-group
                    label="How many calls at once"
                    label-for="ai-max-concurrent"
                    label-class="small font-weight-bold mb-25"
                    class="mb-0"
                  >
                    <b-form-input
                      id="ai-max-concurrent"
                      type="number"
                      min="1"
                      max="2"
                      v-model.number="form.maxConcurrent"
                    />
                    <b-form-text class="small">1 or 2 — keeps shared phone lines free.</b-form-text>
                  </b-form-group>
                </b-card>
              </b-col>
            </b-row>

            <b-card class="ai-calling-section mb-1" body-class="p-1">
              <div class="ai-calling-section__title mb-25">Outbound phone numbers</div>
              <p class="text-muted small mb-75">The numbers candidates will see. Both are required.</p>
              <b-row>
                <b-col cols="12" md="6" class="mb-50 mb-md-0">
                  <b-form-group
                    label="Number 1"
                    label-for="ai-num-1"
                    label-class="small font-weight-bold mb-25"
                    class="mb-0"
                  >
                    <b-form-input id="ai-num-1" v-model="form.number1" :placeholder="numPlaceholder" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                  <b-form-group
                    label="Number 2"
                    label-for="ai-num-2"
                    label-class="small font-weight-bold mb-25"
                    class="mb-0"
                  >
                    <b-form-input id="ai-num-2" v-model="form.number2" :placeholder="numPlaceholder2" />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card>

            <b-card class="ai-calling-section mb-0" body-class="p-1">
              <div class="d-flex flex-wrap align-items-start justify-content-between mb-75">
                <div class="mr-1">
                  <div class="ai-calling-section__title">Who talks to the candidate?</div>
                  <p class="text-muted small mb-0">
                    Create agents in
                    <b-button
                      v-if="embedded"
                      variant="link"
                      class="p-0 align-baseline ai-calling-link"
                      @click="goToAgentsTab"
                    >
                      Agents
                    </b-button>
                    <strong v-else>Agents</strong>,
                    then pick one — or type a name.
                  </p>
                </div>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  class="flex-shrink-0 mt-25 mt-sm-0"
                  :disabled="loadingAgents"
                  aria-label="Refresh agents"
                  @click="refreshAgents"
                >
                  <b-spinner small v-if="loadingAgents" />
                  <feather-icon v-else icon="RefreshCwIcon" size="14" />
                </b-button>
              </div>

              <b-alert
                v-if="agentsLoadIsError && agentsLoadMsg"
                variant="warning"
                show
                class="py-50 mb-75"
              >
                <span class="small">{{ agentsLoadMsg }}</span>
              </b-alert>
              <p v-else-if="agentsLoadMsg" class="text-muted small mb-75">{{ agentsLoadMsg }}</p>

              <b-row>
                <b-col
                  v-for="slot in agentAssignmentSlots"
                  :key="slot.key"
                  cols="12"
                  md="4"
                  class="mb-1 mb-md-0"
                >
                  <b-form-group
                    :label="slot.label"
                    :label-for="slot.inputId"
                    label-class="small font-weight-bold mb-25"
                    class="mb-0"
                  >
                    <v-select
                      v-if="showAgentSelect"
                      v-model="form[slot.key]"
                      :options="agentOptions"
                      :reduce="opt => opt.value"
                      label="text"
                      :placeholder="agentPlaceholder"
                      class="ai-agent-select"
                      :clearable="true"
                      taggable
                      @open="onAgentPickerOpen"
                      @option:created="opt => onTypedAgent(slot.key, opt)"
                    >
                      <template #option="{ text, code }">
                        <div>
                          <div>{{ text }}</div>
                          <small v-if="code" class="ai-calling-muted-id">{{ code }}</small>
                        </div>
                      </template>
                    </v-select>
                    <b-form-input
                      v-else
                      :id="slot.inputId"
                      v-model="form[slot.key]"
                      :placeholder="agentPlaceholder"
                      list="ai-agent-id-list"
                    />
                    <b-form-text class="small">{{ slot.hint }}</b-form-text>
                  </b-form-group>
                </b-col>
              </b-row>
              <datalist v-if="agentOptions.length" id="ai-agent-id-list">
                <option
                  v-for="opt in agentOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.text }}
                </option>
              </datalist>

              <div class="mt-75">
                <b-button
                  v-if="!showLegacyAgentField"
                  variant="link"
                  size="sm"
                  class="p-0 ai-calling-link"
                  @click="showAdvanced = true"
                >
                  Advanced
                </b-button>
                <div v-else class="ai-calling-advanced rounded px-75 py-75">
                  <div class="d-flex align-items-center justify-content-between mb-50">
                    <small class="font-weight-bold mb-0">Company fallback</small>
                    <b-button
                      v-if="!form.agentId"
                      variant="link"
                      size="sm"
                      class="p-0 ai-calling-link"
                      @click="showAdvanced = false"
                    >
                      Hide
                    </b-button>
                  </div>
                  <b-form-group
                    label="Fallback agent"
                    label-for="ai-agent"
                    label-class="small font-weight-bold mb-25"
                    class="mb-0"
                  >
                    <v-select
                      v-if="showAgentSelect"
                      v-model="form.agentId"
                      :options="agentOptions"
                      :reduce="opt => opt.value"
                      label="text"
                      :placeholder="agentPlaceholder"
                      class="ai-agent-select"
                      :clearable="true"
                      taggable
                      @open="onAgentPickerOpen"
                      @option:created="opt => onTypedAgent('agentId', opt)"
                    >
                      <template #option="{ text, code }">
                        <div>
                          <div>{{ text }}</div>
                          <small v-if="code" class="ai-calling-muted-id">{{ code }}</small>
                        </div>
                      </template>
                    </v-select>
                    <b-form-input
                      v-else
                      id="ai-agent"
                      v-model="form.agentId"
                      :placeholder="agentPlaceholder"
                      list="ai-agent-id-list"
                    />
                    <b-form-text class="small">Used only if a call type above has no agent.</b-form-text>
                  </b-form-group>
                </div>
              </div>
            </b-card>
          </b-tab>

          <!-- 2. Voice -->
          <b-tab :title="wizardSteps[1]">
            <b-card class="ai-calling-section mb-0" body-class="p-1">
              <div class="ai-calling-section__title mb-25">Voice per agent</div>
              <p class="text-muted small mb-75">
                Each agent has its own language. Default voice is Maya
                (<code>Indus-en-maya</code> / <code>Indus-hi-maya</code>).
                Language stays per agent (en / hi). Set the same pair on Indus Config.
              </p>
              <div class="small font-weight-bold mb-50">Defaults for new agents</div>
              <b-row>
                <b-col md="4" class="mb-1">
                  <b-form-group label="Language" label-for="ai-language" label-class="small font-weight-bold mb-25" class="mb-0">
                    <b-form-select
                      id="ai-language"
                      v-model="form.transcriptLanguage"
                      :options="languageOptions"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4" class="mb-1">
                  <b-form-group label="Voice" label-for="ai-voice" label-class="small font-weight-bold mb-25" class="mb-0">
                    <b-form-select
                      id="ai-voice"
                      v-model="form.voice"
                      :options="voiceSelectOptions"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <div class="small font-weight-bold mb-50">Agents</div>
              <p v-if="!voiceAgentRows.length" class="text-muted small mb-1">
                Load agents on Setup, or create them on the Agents tab.
              </p>
              <div
                v-for="row in voiceAgentRows"
                :key="row.id"
                class="ai-agent-voice-row d-flex flex-wrap align-items-end mb-75"
              >
                <div class="ai-agent-voice-row__name mr-1 mb-50">
                  <div class="small font-weight-bold text-break">{{ row.name }}</div>
                  <div class="text-muted" style="font-size: 0.72rem;">{{ row.id }}</div>
                </div>
                <b-form-group label="Language" label-class="small font-weight-bold mb-25" class="mb-50 mr-1">
                  <b-form-select
                    :value="agentVoiceLanguage(row.id)"
                    :options="languageOptions"
                    @change="onAgentVoiceLanguage(row.id, $event)"
                  />
                </b-form-group>
                <b-form-group label="Voice" label-class="small font-weight-bold mb-25" class="mb-50">
                  <b-form-select
                    :value="agentVoiceCode(row.id)"
                    :options="voiceSelectOptionsForAgent(row.id)"
                    @change="onAgentVoiceCode(row.id, $event)"
                  />
                </b-form-group>
              </div>
              <div class="small font-weight-bold mb-50 mt-50">Audio defaults</div>
              <b-row>
                <b-col md="4" class="mb-1">
                  <b-form-group label="Voice quality" label-for="ai-model" label-class="small font-weight-bold mb-25" class="mb-0">
                    <b-form-select id="ai-model" v-model="form.model" :options="modelOptions" />
                  </b-form-group>
                </b-col>
                <b-col md="4" class="mb-1">
                  <b-form-group label="Audio format" label-for="ai-format" label-class="small font-weight-bold mb-25" class="mb-0">
                    <b-form-select id="ai-format" v-model="form.outputFormat" :options="formatOptions" />
                  </b-form-group>
                </b-col>
                <b-col md="3" class="mb-1">
                  <b-form-group label="Speed" label-for="ai-speed" label-class="small font-weight-bold mb-25" class="mb-0">
                    <b-form-input id="ai-speed" type="number" step="0.1" v-model.number="form.speed" />
                  </b-form-group>
                </b-col>
                <b-col md="3" class="mb-1">
                  <b-form-group label="Pitch" label-for="ai-pitch" label-class="small font-weight-bold mb-25" class="mb-0">
                    <b-form-input id="ai-pitch" type="number" step="1" v-model.number="form.pitchShift" />
                  </b-form-group>
                </b-col>
                <b-col md="3" class="mb-1">
                  <b-form-group label="Volume" label-for="ai-loudness" label-class="small font-weight-bold mb-25" class="mb-0">
                    <b-form-input id="ai-loudness" type="number" step="1" v-model.number="form.loudnessDb" />
                  </b-form-group>
                </b-col>
                <b-col md="3" class="mb-1">
                  <b-form-group label="Audio quality" label-for="ai-sample" label-class="small font-weight-bold mb-25" class="mb-0">
                    <b-form-select id="ai-sample" v-model.number="form.sampleRate" :options="sampleRateOptions" />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card>
          </b-tab>

          <!-- 3. Fields -->
          <b-tab :title="wizardSteps[2]">
            <b-alert show class="ai-calling-note py-75 mb-1">
              <div class="d-flex align-items-start">
                <feather-icon icon="InfoIcon" size="16" class="ai-calling-brand-icon mr-50 flex-shrink-0 mt-25" />
                <div class="small">
                  These lists are sent when an agent is created and when a call starts.
                  Empty config falls back to the current official defaults. Indus allows
                  up to {{ form.schemaSlotLimit || 15 }} infields and outcomes.
                </div>
              </div>
            </b-alert>

            <b-card class="ai-calling-section mb-1" body-class="p-1">
              <div class="d-flex flex-wrap align-items-center justify-content-between mb-75">
                <div>
                  <div class="ai-calling-section__title mb-0">Screening infields</div>
                  <p class="text-muted small mb-0">{{ form.screeningInfields.length }} / {{ form.schemaSlotLimit }} keys on create</p>
                </div>
                <b-button size="sm" variant="outline-primary" @click="addCatalogInfield('screeningInfields')">
                  Add field
                </b-button>
              </div>
              <b-table :items="form.screeningInfields" :fields="infieldEditorFields" small responsive class="mb-0">
                <template #cell(key)="data">
                  <b-form-input v-model="data.item.key" size="sm" placeholder="field_name" />
                </template>
                <template #cell(label)="data">
                  <b-form-input v-model="data.item.label" size="sm" placeholder="Label" />
                </template>
                <template #cell(type)="data">
                  <b-form-select v-model="data.item.type" :options="infieldTypeOptions" size="sm" />
                </template>
                <template #cell(required)="data">
                  <b-form-checkbox v-model="data.item.required" />
                </template>
                <template #cell(actions)="data">
                  <b-button size="sm" variant="outline-danger" @click="removeCatalogRow('screeningInfields', data.index)">Remove</b-button>
                </template>
              </b-table>
            </b-card>

            <b-card class="ai-calling-section mb-1" body-class="p-1">
              <div class="d-flex flex-wrap align-items-center justify-content-between mb-75">
                <div>
                  <div class="ai-calling-section__title mb-0">Drive infields</div>
                  <p class="text-muted small mb-0">{{ form.driveInfields.length }} / {{ form.schemaSlotLimit }} keys on create</p>
                </div>
                <b-button size="sm" variant="outline-primary" @click="addCatalogInfield('driveInfields')">
                  Add field
                </b-button>
              </div>
              <b-table :items="form.driveInfields" :fields="infieldEditorFields" small responsive class="mb-0">
                <template #cell(key)="data">
                  <b-form-input v-model="data.item.key" size="sm" placeholder="field_name" />
                </template>
                <template #cell(label)="data">
                  <b-form-input v-model="data.item.label" size="sm" placeholder="Label" />
                </template>
                <template #cell(type)="data">
                  <b-form-select v-model="data.item.type" :options="infieldTypeOptions" size="sm" />
                </template>
                <template #cell(required)="data">
                  <b-form-checkbox v-model="data.item.required" />
                </template>
                <template #cell(actions)="data">
                  <b-button size="sm" variant="outline-danger" @click="removeCatalogRow('driveInfields', data.index)">Remove</b-button>
                </template>
              </b-table>
            </b-card>

            <b-card class="ai-calling-section mb-1" body-class="p-1">
              <div class="d-flex flex-wrap align-items-center justify-content-between mb-75">
                <div>
                  <div class="ai-calling-section__title mb-0">Drive extras on initiate</div>
                  <p class="text-muted small mb-0">Added to screening keys only for drive calls</p>
                </div>
                <b-button size="sm" variant="outline-primary" @click="addCatalogInfield('driveInitiateInfields')">
                  Add extra
                </b-button>
              </div>
              <b-table :items="form.driveInitiateInfields" :fields="infieldEditorFields" small responsive class="mb-0">
                <template #cell(key)="data">
                  <b-form-input v-model="data.item.key" size="sm" placeholder="field_name" />
                </template>
                <template #cell(label)="data">
                  <b-form-input v-model="data.item.label" size="sm" placeholder="Label" />
                </template>
                <template #cell(type)="data">
                  <b-form-select v-model="data.item.type" :options="infieldTypeOptions" size="sm" />
                </template>
                <template #cell(required)="data">
                  <b-form-checkbox v-model="data.item.required" />
                </template>
                <template #cell(actions)="data">
                  <b-button size="sm" variant="outline-danger" @click="removeCatalogRow('driveInitiateInfields', data.index)">Remove</b-button>
                </template>
              </b-table>
            </b-card>

            <b-card class="ai-calling-section mb-1" body-class="p-1">
              <div class="d-flex flex-wrap align-items-center justify-content-between mb-75">
                <div>
                  <div class="ai-calling-section__title mb-0">Screening outcomes</div>
                  <p class="text-muted small mb-0">{{ form.screeningOutcomes.length }} / {{ form.schemaSlotLimit }}</p>
                </div>
                <b-button size="sm" variant="outline-primary" @click="addCatalogOutcome('screeningOutcomes')">
                  Add outcome
                </b-button>
              </div>
              <b-table :items="form.screeningOutcomes" :fields="outcomeEditorFields" small responsive class="mb-0">
                <template #cell(outcome_name)="data">
                  <b-form-input v-model="data.item.outcome_name" size="sm" placeholder="outcome_name" />
                </template>
                <template #cell(outcome_description)="data">
                  <b-form-input v-model="data.item.outcome_description" size="sm" placeholder="Label" />
                </template>
                <template #cell(outcome_type)="data">
                  <b-form-select v-model="data.item.outcome_type" :options="outcomeTypeOptions" size="sm" />
                </template>
                <template #cell(outcome_enum)="data">
                  <b-form-input
                    :value="enumToText(data.item.outcome_enum)"
                    size="sm"
                    placeholder="Completed, Call Back"
                    :disabled="data.item.outcome_type !== 'FIXED'"
                    @change="setOutcomeEnum(data.item, $event)"
                  />
                </template>
                <template #cell(actions)="data">
                  <b-button size="sm" variant="outline-danger" @click="removeCatalogRow('screeningOutcomes', data.index)">Remove</b-button>
                </template>
              </b-table>
            </b-card>

            <b-card class="ai-calling-section mb-1" body-class="p-1">
              <div class="d-flex flex-wrap align-items-center justify-content-between mb-75">
                <div>
                  <div class="ai-calling-section__title mb-0">Drive outcomes</div>
                  <p class="text-muted small mb-0">{{ form.driveOutcomes.length }} / {{ form.schemaSlotLimit }}</p>
                </div>
                <b-button size="sm" variant="outline-primary" @click="addCatalogOutcome('driveOutcomes')">
                  Add outcome
                </b-button>
              </div>
              <b-table :items="form.driveOutcomes" :fields="outcomeEditorFields" small responsive class="mb-0">
                <template #cell(outcome_name)="data">
                  <b-form-input v-model="data.item.outcome_name" size="sm" placeholder="outcome_name" />
                </template>
                <template #cell(outcome_description)="data">
                  <b-form-input v-model="data.item.outcome_description" size="sm" placeholder="Label" />
                </template>
                <template #cell(outcome_type)="data">
                  <b-form-select v-model="data.item.outcome_type" :options="outcomeTypeOptions" size="sm" />
                </template>
                <template #cell(outcome_enum)="data">
                  <b-form-input
                    :value="enumToText(data.item.outcome_enum)"
                    size="sm"
                    placeholder="Completed, Call Back"
                    :disabled="data.item.outcome_type !== 'FIXED'"
                    @change="setOutcomeEnum(data.item, $event)"
                  />
                </template>
                <template #cell(actions)="data">
                  <b-button size="sm" variant="outline-danger" @click="removeCatalogRow('driveOutcomes', data.index)">Remove</b-button>
                </template>
              </b-table>
            </b-card>

            <b-row>
              <b-col md="6" class="mb-1">
                <b-card class="ai-calling-section h-100 mb-0" body-class="p-1">
                  <div class="d-flex flex-wrap align-items-center justify-content-between mb-75">
                    <div class="ai-calling-section__title mb-0">Languages</div>
                    <b-button size="sm" variant="outline-primary" @click="addCatalogLanguage">Add</b-button>
                  </div>
                  <div v-for="(row, index) in form.languages" :key="`lang-${index}`" class="d-flex align-items-center mb-50">
                    <b-form-input v-model="row.value" size="sm" class="mr-50" placeholder="en" />
                    <b-form-input v-model="row.text" size="sm" class="mr-50" placeholder="English" />
                    <b-button size="sm" variant="outline-danger" @click="removeCatalogRow('languages', index)">Remove</b-button>
                  </div>
                </b-card>
              </b-col>
              <b-col md="6" class="mb-1">
                <b-card class="ai-calling-section h-100 mb-0" body-class="p-1">
                  <div class="d-flex flex-wrap align-items-center justify-content-between mb-75">
                    <div class="ai-calling-section__title mb-0">Voices</div>
                    <b-button size="sm" variant="outline-primary" @click="addCatalogVoice">Add</b-button>
                  </div>
                  <div v-for="(row, index) in form.voices" :key="`voice-${index}`" class="d-flex align-items-center mb-50">
                    <b-form-input v-model="row.id" size="sm" class="mr-50" placeholder="Indus-en-maya" />
                    <b-form-input v-model="row.language" size="sm" class="mr-50" placeholder="en" style="max-width: 4.5rem;" />
                    <b-form-input v-model="row.name" size="sm" class="mr-50" placeholder="Maya" />
                    <b-button size="sm" variant="outline-danger" @click="removeCatalogRow('voices', index)">Remove</b-button>
                  </div>
                </b-card>
              </b-col>
            </b-row>

            <b-card class="ai-calling-section mb-1" body-class="p-1">
              <div class="ai-calling-section__title mb-25">Required script tokens</div>
              <p class="text-muted small mb-75">Comma-separated {{ '{' }}{{ '{' }}tokens{{ '}' }}{{ '}' }} the script validator expects.</p>
              <b-row>
                <b-col md="6" class="mb-50">
                  <b-form-group label="Screening" label-class="small font-weight-bold mb-25" class="mb-0">
                    <b-form-input
                      :value="(form.requiredPromptTokens.fresh || []).join(', ')"
                      placeholder="agent_name, company, role"
                      @change="setTokenList('requiredPromptTokens', 'fresh', $event)"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6" class="mb-50">
                  <b-form-group label="Drive" label-class="small font-weight-bold mb-25" class="mb-0">
                    <b-form-input
                      :value="(form.requiredPromptTokens.drive || []).join(', ')"
                      placeholder="drive_name, drive_dates, drive_location"
                      @change="setTokenList('requiredPromptTokens', 'drive', $event)"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6" class="mb-50">
                  <b-form-group label="Required sections" label-class="small font-weight-bold mb-25" class="mb-0">
                    <b-form-input
                      :value="(form.requiredPromptSections || []).join(', ')"
                      placeholder="ROLE, STYLE, JOB DETAILS"
                      @change="setTokenList('requiredPromptSections', null, $event)"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6" class="mb-50">
                  <b-form-group label="Agent slots" label-class="small font-weight-bold mb-25" class="mb-0">
                    <b-form-text class="small">{{ agentSlotSummary }}</b-form-text>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card>

            <b-card class="ai-calling-section mb-0" body-class="p-1">
              <div class="ai-calling-section__title mb-25">Job picker opening presets</div>
              <p class="text-muted small mb-75">Choices on the job AI Calling tab. Templates tab is still for full reusable scripts.</p>
              <div v-for="group in presetEditorGroups" :key="group.key" class="mb-75">
                <div class="small font-weight-bold mb-50">{{ group.label }}</div>
                <div v-for="(row, index) in form.presetPromptLines[group.key]" :key="`${group.key}-${index}`" class="d-flex flex-wrap align-items-center mb-50">
                  <b-form-input v-model="row.id" size="sm" class="mr-50 mb-25" placeholder="id" style="max-width: 8rem;" />
                  <b-form-input v-model="row.label" size="sm" class="mr-50 mb-25" placeholder="Label" />
                  <b-form-input v-model="row.description" size="sm" class="mr-50 mb-25" placeholder="Description" />
                  <b-button size="sm" variant="outline-danger" class="mb-25" @click="removePresetRow(group.key, index)">Remove</b-button>
                </div>
                <b-button size="sm" variant="outline-secondary" @click="addPresetRow(group.key)">Add {{ group.label.toLowerCase() }} preset</b-button>
              </div>
            </b-card>
          </b-tab>

          <!-- 4. Script -->
          <b-tab :title="wizardSteps[3]">
            <b-card class="ai-calling-section mb-0" body-class="p-1">
              <div class="ai-calling-section__title mb-25">Company default script</div>
              <p class="text-muted small mb-75">Jobs can still use their own opening line.</p>

              <b-form-group
                label="What the agent should do"
                label-for="ai-system-prompt"
                label-class="small font-weight-bold mb-25"
                class="mb-1"
              >
                <b-form-textarea
                  id="ai-system-prompt"
                  v-model="form.systemPrompt"
                  rows="8"
                  class="ai-prompt-textarea"
                  :placeholder="systemPromptPlaceholder"
                />
              </b-form-group>

              <b-form-group
                label="Opening line"
                label-for="ai-first-message"
                label-class="small font-weight-bold mb-25"
                class="mb-1"
              >
                <b-form-textarea
                  id="ai-first-message"
                  v-model="form.firstMessage"
                  rows="3"
                  class="ai-prompt-textarea"
                  :placeholder="firstMessagePlaceholder"
                />
              </b-form-group>

              <div class="mb-1">
                <small class="text-muted d-block mb-25">These fill in automatically from the job</small>
                <div class="d-flex flex-wrap">
                  <b-badge
                    v-for="v in variableList"
                    :key="v.key"
                    variant="light-secondary"
                    class="mr-25 mb-25 ai-var-chip"
                  >
                    {{ varToken(v.key) }}
                  </b-badge>
                </div>
              </div>

              <b-row>
                <b-col md="6" class="mb-1">
                  <b-form-group
                    label="Default screening opening"
                    label-for="ai-fresh-script"
                    label-class="small font-weight-bold mb-25"
                    class="mb-0"
                  >
                    <b-form-textarea
                      id="ai-fresh-script"
                      v-model="form.defaultFreshScript"
                      rows="2"
                      class="ai-prompt-textarea"
                      placeholder="Optional opening line for screening calls…"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6" class="mb-1">
                  <b-form-group
                    label="Default drive opening"
                    label-for="ai-drive-script"
                    label-class="small font-weight-bold mb-25"
                    class="mb-0"
                  >
                    <b-form-textarea
                      id="ai-drive-script"
                      v-model="form.defaultDriveScript"
                      rows="2"
                      class="ai-prompt-textarea"
                      placeholder="Optional opening line for walk-in / drive calls…"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-button
                size="sm"
                variant="outline-secondary"
                class="mb-50"
                @click="showPreview = !showPreview"
              >
                {{ showPreview ? 'Hide preview' : 'Preview with sample values' }}
              </b-button>
              <div v-if="showPreview" class="ai-preview-box border rounded p-75">
                <small class="text-muted d-block mb-25">Opening line</small>
                <pre class="ai-preview-pre small mb-75">{{ previewFirstMessage }}</pre>
                <small class="text-muted d-block mb-25">Script</small>
                <pre class="ai-preview-pre small mb-0">{{ previewSystemPrompt }}</pre>
              </div>
            </b-card>
          </b-tab>

          <!-- 5. Prompt templates -->
          <b-tab :title="wizardSteps[4]">
            <div class="d-flex flex-wrap justify-content-between align-items-start mb-1">
              <p class="text-muted small mb-50 mr-1">
                Reusable scripts recruiters can pick on a job.
              </p>
              <b-button size="sm" variant="outline-primary" @click="openTemplateForm()">
                <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                Add template
              </b-button>
            </div>

            <b-table
              v-if="promptTemplates.length"
              :items="promptTemplates"
              :fields="templateFields"
              small
              responsive
              class="mb-1"
            >
              <template #cell(call_type)="data">
                <span class="text-capitalize">{{ data.item.call_type === 'drive' ? 'Drive' : 'Screening' }}</span>
              </template>
              <template #cell(active)="data">
                <b-badge :variant="data.item.active !== false ? 'light-success' : 'light-secondary'">
                  {{ data.item.active !== false ? 'Active' : 'Hidden' }}
                </b-badge>
              </template>
              <template #cell(actions)="data">
                <b-button size="sm" variant="outline-primary" class="mr-25" @click="openTemplateForm(data.item)">
                  Edit
                </b-button>
                <b-button size="sm" variant="outline-danger" @click="removeTemplate(data.item)">
                  Remove
                </b-button>
              </template>
            </b-table>
            <b-alert
              v-else
              show
              variant="light"
              class="ai-calling-empty mb-1"
            >
              <div class="text-center py-1 px-50">
                <feather-icon icon="FileTextIcon" size="22" class="text-muted mb-50" />
                <strong class="d-block mb-25">No templates yet</strong>
                <p class="text-muted small mb-1">Recruiters will use the company default script until you add some.</p>
                <b-button variant="primary" @click="openTemplateForm()">
                  <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                  Add template
                </b-button>
              </div>
            </b-alert>

            <b-card v-if="templateFormOpen" body-class="p-1" class="ai-calling-section mb-0">
              <div class="ai-calling-section__title mb-75">{{ templateForm.id ? 'Edit template' : 'New template' }}</div>
              <b-row>
                <b-col md="6" class="mb-1">
                  <b-form-group label="Name" label-class="small font-weight-bold mb-25" class="mb-0">
                    <b-form-input v-model="templateForm.name" />
                  </b-form-group>
                </b-col>
                <b-col md="6" class="mb-1">
                  <b-form-group label="Call type" label-class="small font-weight-bold mb-25" class="mb-0">
                    <b-form-select v-model="templateForm.call_type" :options="templateCallTypeOptions" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" class="mb-1">
                  <b-form-group label="Description" label-class="small font-weight-bold mb-25" class="mb-0">
                    <b-form-input v-model="templateForm.description" placeholder="One line for recruiters" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" class="mb-1">
                  <b-form-group label="Opening message" label-class="small font-weight-bold mb-25" class="mb-0">
                    <b-form-textarea v-model="templateForm.first_message" rows="3" class="ai-prompt-textarea" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" class="mb-1">
                  <b-form-group label="What the agent should do" label-class="small font-weight-bold mb-25" class="mb-0">
                    <b-form-textarea v-model="templateForm.system_prompt" rows="6" class="ai-prompt-textarea" />
                  </b-form-group>
                </b-col>
              </b-row>
              <div class="d-flex flex-wrap">
                <b-button variant="primary" class="mr-50" :disabled="savingTemplate" @click="saveTemplate">
                  <b-spinner v-if="savingTemplate" small class="mr-25" />
                  Save template
                </b-button>
                <b-button variant="outline-secondary" @click="templateFormOpen = false">
                  Cancel
                </b-button>
              </div>
            </b-card>
          </b-tab>

          <!-- 6. Access -->
          <b-tab :title="wizardSteps[5]">
            <div class="d-flex flex-wrap justify-content-between align-items-start mb-1">
              <div class="mr-1 mb-50">
                <div class="ai-calling-section__title">Who else can use this?</div>
                <p class="text-muted small mb-0">
                  Admins already have full access. Add people who should view, edit the script, or place calls.
                </p>
              </div>
              <b-button
                size="sm"
                variant="outline-primary"
                aria-label="Add user grant"
                @click="addSpecialUser"
              >
                <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                Add user
              </b-button>
            </div>

            <b-table
              v-if="form.specialUsers.length"
              :items="form.specialUsers"
              :fields="specialUserFields"
              small
              responsive
              class="mb-0"
            >
              <template #cell(emp_id)="data">
                <v-select
                  v-model="data.item.emp_id"
                  :options="employeeOptionsForRow(data.index)"
                  :reduce="opt => opt.value"
                  label="text"
                  placeholder="Select employee…"
                  class="ai-emp-select"
                  :clearable="true"
                />
              </template>
              <template #cell(view)="data">
                <b-form-checkbox v-model="data.item.view" />
              </template>
              <template #cell(prompt)="data">
                <b-form-checkbox v-model="data.item.prompt" />
              </template>
              <template #cell(run)="data">
                <b-form-checkbox v-model="data.item.run" />
              </template>
              <template #cell(actions)="data">
                <b-button size="sm" variant="outline-danger" @click="removeSpecialUser(data.index)">
                  Remove
                </b-button>
              </template>
            </b-table>
            <b-alert
              v-else
              show
              variant="light"
              class="ai-calling-empty mb-0"
            >
              <div class="text-center py-1 px-50">
                <feather-icon icon="UserPlusIcon" size="22" class="text-muted mb-50" />
                <strong class="d-block mb-25">No extra people yet</strong>
                <p class="text-muted small mb-1">Admins already have full access. Add anyone else who needs to view, edit the script, or place calls.</p>
                <b-button variant="primary" @click="addSpecialUser">
                  <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                  Add user
                </b-button>
              </div>
            </b-alert>
          </b-tab>

          <!-- 7. Review -->
          <b-tab :title="wizardSteps[6]">
            <b-card class="ai-calling-review mb-1" body-class="p-1">
              <dl class="ai-review-dl mb-0">
                <div class="d-flex justify-content-between py-50 border-bottom">
                  <dt class="mb-0 text-muted small">AI Calling</dt>
                  <dd class="mb-0">
                    <b-badge :class="form.enabled ? 'ai-calling-on-badge' : ''" :variant="form.enabled ? undefined : 'light-secondary'">
                      {{ form.enabled ? 'On' : 'Off' }}
                    </b-badge>
                  </dd>
                </div>
                <div class="d-flex justify-content-between py-50 border-bottom">
                  <dt class="mb-0 text-muted small">Outbound numbers</dt>
                  <dd class="mb-0 small font-weight-bold text-right">{{ reviewNumbers }}</dd>
                </div>
                <div class="d-flex justify-content-between py-50 border-bottom">
                  <dt class="mb-0 text-muted small">Calls at once</dt>
                  <dd class="mb-0 small font-weight-bold">{{ form.maxConcurrent === 1 ? 1 : 2 }}</dd>
                </div>
                <div class="d-flex justify-content-between align-items-start py-50 border-bottom">
                  <dt class="mb-0 text-muted small">Screening</dt>
                  <dd class="mb-0 text-right">
                    <div class="small font-weight-bold text-break">{{ agentDisplayName(form.agentIdFresh || form.agentId) }}</div>
                    <div v-if="agentDisplayCode(form.agentIdFresh || form.agentId)" class="ai-calling-muted-id">
                      {{ agentDisplayCode(form.agentIdFresh || form.agentId) }}
                    </div>
                  </dd>
                </div>
                <div class="d-flex justify-content-between align-items-start py-50 border-bottom">
                  <dt class="mb-0 text-muted small">Drive</dt>
                  <dd class="mb-0 text-right">
                    <div class="small font-weight-bold text-break">{{ agentDisplayName(form.agentIdDrive) }}</div>
                    <div v-if="agentDisplayCode(form.agentIdDrive)" class="ai-calling-muted-id">
                      {{ agentDisplayCode(form.agentIdDrive) }}
                    </div>
                  </dd>
                </div>
                <div class="d-flex justify-content-between align-items-start py-50 border-bottom">
                  <dt class="mb-0 text-muted small">Other</dt>
                  <dd class="mb-0 text-right">
                    <div class="small font-weight-bold text-break">{{ agentDisplayName(form.agentIdOther) }}</div>
                    <div v-if="agentDisplayCode(form.agentIdOther)" class="ai-calling-muted-id">
                      {{ agentDisplayCode(form.agentIdOther) }}
                    </div>
                  </dd>
                </div>
                <div class="d-flex justify-content-between py-50 border-bottom">
                  <dt class="mb-0 text-muted small">Language / voice</dt>
                  <dd class="mb-0 small font-weight-bold text-right">{{ languageReviewLabel }} · {{ voiceReviewLabel }}</dd>
                </div>
                <div class="d-flex justify-content-between py-50 border-bottom">
                  <dt class="mb-0 text-muted small">Infields / outcomes</dt>
                  <dd class="mb-0 small font-weight-bold text-right">
                    {{ form.screeningInfields.length }} screening fields · {{ form.screeningOutcomes.length }} outcomes
                  </dd>
                </div>
                <div class="d-flex justify-content-between py-50 border-bottom">
                  <dt class="mb-0 text-muted small">Script</dt>
                  <dd class="mb-0 small text-muted text-right">
                    {{ systemPromptLen }} characters · opening line {{ firstMessageLen }}
                  </dd>
                </div>
                <div class="d-flex justify-content-between py-50">
                  <dt class="mb-0 text-muted small">Extra people with access</dt>
                  <dd class="mb-0 small font-weight-bold">{{ grantedUserCount }}</dd>
                </div>
              </dl>
            </b-card>

            <b-alert
              v-if="!form.agentIdFresh && !form.agentId && !form.agentIdDrive && !form.agentIdOther"
              variant="warning"
              show
              class="py-75 mb-1"
            >
              No agent assigned yet — pick a Screening, Drive, or Other agent on Setup before placing calls.
            </b-alert>

            <p class="text-muted small mb-1">
              Save applies to the whole company. Jobs use the agent you picked for each call type.
            </p>

            <b-button
              variant="primary"
              type="submit"
              :disabled="saving || loading"
            >
              <b-spinner small v-if="saving" class="mr-25" />
              <feather-icon v-else icon="SaveIcon" size="14" class="mr-25" />
              Save settings
            </b-button>
          </b-tab>
        </b-tabs>

        <div class="ai-wizard-footer d-flex flex-wrap align-items-center justify-content-between mt-1 pt-75 border-top">
          <div>
            <b-button
              size="sm"
              variant="outline-secondary"
              class="mr-50"
              :disabled="activeTab === 0"
              @click="goPrev"
            >
              Back
            </b-button>
            <b-button
              v-if="activeTab < wizardSteps.length - 1"
              size="sm"
              variant="outline-primary"
              @click="goNext"
            >
              Next
            </b-button>
          </div>
          <span
            v-if="statusMsg"
            class="small d-inline-flex align-items-center"
            :class="statusMsg === 'Saved' || statusMsg === 'Loaded' ? 'text-success' : 'text-muted'"
          >
            <feather-icon
              :icon="statusMsg === 'Saved' ? 'CheckCircleIcon' : statusMsg === 'Loaded' ? 'DownloadCloudIcon' : 'InfoIcon'"
              size="14"
              class="mr-25"
            />
            {{ statusMsg }}
          </span>
        </div>
      </b-form>
      </b-overlay>
    </div>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BFormCheckbox,
  BButton,
  BAlert,
  BOverlay,
  BProgress,
  BTable,
  BSpinner,
  BBadge,
  BTabs,
  BTab,
  BCard,
  BFormText,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import adminConfigService from '@/services/admin-config-service'
import aiCallingService, {
  AGENTS_LIST_UNAVAILABLE_MSG,
  extractIndusAgentsList,
  isDeletedIndusAgent,
  parseIndusAgentsListResponse,
  rememberDeletedIndusAgents,
} from '@/services/ai-calling-service'
import UserService from '@/services/user-service'
import { canManageAiCallingConfig } from '@/utils/admin-settings-permissions'
import {
  AI_CALLING_VARIABLES,
  DEFAULT_SYSTEM_PROMPT,
  DEFAULT_FIRST_MESSAGE,
  DEFAULT_DRIVE_FIRST_MESSAGE,
  isStockHinglishPrompt,
  fillPromptTemplate,
  TRANSCRIPT_LANGUAGE_OPTIONS,
  normalizeTranscriptLanguage,
  defaultVoiceForLanguage,
  voiceMatchesLanguage,
  voiceSelectOptionsForLanguage,
  humanVoiceLabel,
  canonicalizeIndusVoiceId,
  normalizeAgentVoiceMap,
  normalizeAgentVoiceEntry,
  getAgentVoiceSettings,
  defaultAgentVoicePair,
  DEFAULT_ENGLISH_VOICE_ID,
  SCREENING_CREATE_INFIELD_KEYS,
  DRIVE_CREATE_INFIELD_KEYS,
  INITIATE_INFIELD_KEYS,
  DRIVE_INITIATE_INFIELD_KEYS,
  SCREENING_CREATE_OUTCOMES,
  DRIVE_CREATE_OUTCOMES,
  INDUS_TTS_VOICES,
  PRESET_PROMPT_LINES,
  AI_CALL_TYPES,
  INDUS_SCHEMA_SLOT_LIMIT,
} from '@/utils/ai-calling-prompt-defaults'
import {
  REQUIRED_SECTIONS,
  REQUIRED_VARIABLES_FRESH,
  REQUIRED_VARIABLES_DRIVE,
  RUNTIME_PROMPT_VARIABLES,
} from '@/utils/ai-calling-prompt-validator'
import {
  applyAiCallingCatalog,
  serializeAiCallingCatalog,
  resolveCatalogForEditor,
  getLanguageOptions,
  getAgentSlots,
  INFIELD_TYPE_OPTIONS,
  OUTCOME_TYPE_OPTIONS,
} from '@/utils/ai-calling-catalog'

/** Same active-user convention as MyCalls / Attendance (allusers shape). */
function isActiveUser(u) {
  const s = u.status || u.employee_status || u.is_active
  return s === 'Active' || s === 'active' || s === true || s === 'ACTIVE'
}

function employeeLabel(u) {
  return (u && (u.name || u.emp_name || u.title || u.emp_id)) || ''
}

const DEFAULT_FORM = () => ({
  enabled: true,
  number1: '',
  number2: '',
  maxConcurrent: 2,
  voice: DEFAULT_ENGLISH_VOICE_ID,
  model: 'indus-tts-v1',
  outputFormat: 'mp3',
  speed: 1,
  pitchShift: 0,
  loudnessDb: 0,
  sampleRate: 24000,
  agentId: '',
  agentIdFresh: '',
  agentIdDrive: '',
  agentIdOther: '',
  transcriptLanguage: '',
  agentVoiceMap: {},
  systemPrompt: DEFAULT_SYSTEM_PROMPT,
  firstMessage: DEFAULT_FIRST_MESSAGE,
  /** Kept for backward-compat with older job-tab script fields. */
  defaultFreshScript: '',
  defaultDriveScript: '',
  defaultFollowUpScript: '',
  specialUsers: [],
  ...resolveCatalogForEditor(catalogFallbacks()),
})

function catalogFallbacks() {
  return {
    screeningInfields: SCREENING_CREATE_INFIELD_KEYS,
    driveInfields: DRIVE_CREATE_INFIELD_KEYS,
    initiateInfields: INITIATE_INFIELD_KEYS,
    driveInitiateInfields: DRIVE_INITIATE_INFIELD_KEYS,
    screeningOutcomes: SCREENING_CREATE_OUTCOMES,
    driveOutcomes: DRIVE_CREATE_OUTCOMES,
    languages: TRANSCRIPT_LANGUAGE_OPTIONS,
    voices: INDUS_TTS_VOICES,
    requiredPromptSections: REQUIRED_SECTIONS,
    requiredPromptTokensFresh: REQUIRED_VARIABLES_FRESH,
    requiredPromptTokensDrive: REQUIRED_VARIABLES_DRIVE,
    runtimePromptVariables: RUNTIME_PROMPT_VARIABLES,
    presetFresh: PRESET_PROMPT_LINES[AI_CALL_TYPES.FRESH] || [],
    presetDrive: PRESET_PROMPT_LINES[AI_CALL_TYPES.DRIVE] || [],
    schemaSlotLimit: INDUS_SCHEMA_SLOT_LIMIT,
  }
}

export default {
  name: 'AiCallingSettings',
  components: {
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BAlert,
    BOverlay,
    BProgress,
    BTable,
    BSpinner,
    BBadge,
    BTabs,
    BTab,
    BCard,
    BFormText,
    vSelect,
  },
  props: {
    /** When true (Admin Settings tab), skip module title — parent tab labels it. */
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTab: 0,
      wizardSteps: ['Setup', 'Voice', 'Fields', 'Script', 'Templates', 'Who can use this', 'Review'],
      showAdvanced: false,
      infieldTypeOptions: INFIELD_TYPE_OPTIONS,
      outcomeTypeOptions: OUTCOME_TYPE_OPTIONS,
      form: DEFAULT_FORM(),
      loading: false,
      saving: false,
      loadError: '',
      statusMsg: '',
      showPreview: false,
      loadingAgents: false,
      agentsFetched: false,
      agentsLoadMsg: '',
      agentsLoadIsError: false,
      agentOptions: [],
      /** Active employees for dropdown: { value: emp_id, text: Name } */
      employeeOptions: [],
      /** Full name map (active + inactive) so saved grants still resolve labels */
      employeeById: {},
      variableList: AI_CALLING_VARIABLES,
      numPlaceholder: 'e.g. 09876543210',
      numPlaceholder2: 'e.g. 09876543211',
      voicePlaceholder: 'How the agent should sound',
      agentPlaceholder: 'Pick an agent or type a name/id',
      systemPromptPlaceholder: 'Instructions the agent follows on every call…',
      firstMessagePlaceholder: 'Opening line with {{agent_name}}, {{company}}, …',
      promptTemplates: [],
      templatesFetched: false,
      templateFormOpen: false,
      savingTemplate: false,
      templateForm: {
        id: null,
        name: '',
        description: '',
        call_type: 'fresh',
        first_message: '',
        system_prompt: '',
        active: true,
      },
      templateFields: [
        { key: 'name', label: 'Name' },
        { key: 'call_type', label: 'Type' },
        { key: 'description', label: 'Description' },
        { key: 'active', label: 'Status' },
        { key: 'actions', label: '' },
      ],
      englishAgentsEnsured: false,
      templateCallTypeOptions: [
        { value: 'fresh', text: 'Screening call' },
        { value: 'drive', text: 'Drive call' },
      ],
      modelOptions: [
        { value: 'indus-tts-v1', text: 'Standard voice' },
        { value: 'indus-tts-v3', text: 'Newer voice' },
      ],
      formatOptions: [
        { value: 'wav', text: 'wav' },
        { value: 'mp3', text: 'mp3' },
        { value: 'pcm', text: 'pcm' },
      ],
      sampleRateOptions: [
        { value: 24000, text: 'High (recommended)' },
        { value: 16000, text: '16000' },
        { value: 8000, text: '8000' },
      ],
      specialUserFields: [
        { key: 'emp_id', label: 'Employee' },
        { key: 'view', label: 'View' },
        { key: 'prompt', label: 'Edit script' },
        { key: 'run', label: 'Run' },
        { key: 'actions', label: '' },
      ],
    }
  },
  computed: {
    canManage() {
      return canManageAiCallingConfig(this.$can.bind(this))
    },
    previewSystemPrompt() {
      return fillPromptTemplate(this.form.systemPrompt)
    },
    previewFirstMessage() {
      return fillPromptTemplate(this.form.firstMessage)
    },
    reviewNumbers() {
      const a = (this.form.number1 || '').trim() || '—'
      const b = (this.form.number2 || '').trim() || '—'
      return `${a} · ${b}`
    },
    grantedUserCount() {
      return (this.form.specialUsers || []).filter(u => (u.emp_id || '').trim()).length
    },
    systemPromptLen() {
      return (this.form.systemPrompt || '').length
    },
    firstMessageLen() {
      return (this.form.firstMessage || '').length
    },
    showAgentSelect() {
      return !this.agentsLoadIsError
    },
    showLegacyAgentField() {
      return this.showAdvanced || !!(this.form.agentId || '').toString().trim()
    },
    hasSavedAgentIds() {
      return !!(
        (this.form.agentId || '').toString().trim()
        || (this.form.agentIdFresh || '').toString().trim()
        || (this.form.agentIdDrive || '').toString().trim()
        || (this.form.agentIdOther || '').toString().trim()
      )
    },
    voiceReviewLabel() {
      const count = this.voiceAgentRows.length
      return count ? `${count} agent voice${count === 1 ? '' : 's'}` : this.humanVoiceLabel(this.form.voice)
    },
    languageReviewLabel() {
      const match = this.languageOptions.find(o => o.value === this.form.transcriptLanguage)
      return match ? `Default ${match.text}` : 'Per agent'
    },
    languageOptions() {
      return [{ value: '', text: 'Choose language…' }, ...getLanguageOptions(TRANSCRIPT_LANGUAGE_OPTIONS)]
    },
    agentAssignmentSlots() {
      return getAgentSlots()
    },
    agentSlotSummary() {
      return (this.form.agentSlots || []).map(s => s.label || s.key).filter(Boolean).join(' · ') || 'Screening · Drive · Other'
    },
    infieldEditorFields() {
      return [
        { key: 'key', label: 'Key' },
        { key: 'label', label: 'Label' },
        { key: 'type', label: 'Type' },
        { key: 'required', label: 'Required' },
        { key: 'actions', label: '' },
      ]
    },
    outcomeEditorFields() {
      return [
        { key: 'outcome_name', label: 'Key' },
        { key: 'outcome_description', label: 'Label' },
        { key: 'outcome_type', label: 'Type' },
        { key: 'outcome_enum', label: 'Fixed values' },
        { key: 'actions', label: '' },
      ]
    },
    presetEditorGroups() {
      return [
        { key: 'fresh', label: 'Screening' },
        { key: 'drive', label: 'Drive' },
      ]
    },
    voiceSelectOptions() {
      return voiceSelectOptionsForLanguage(this.form.transcriptLanguage)
    },
    voiceOptions() {
      return voiceSelectOptionsForLanguage(this.form.transcriptLanguage).map(o => o.value)
    },
    voiceAgentRows() {
      const seen = new Set()
      const rows = []
      this.agentOptions.forEach((opt) => {
        const id = String(opt.value || '').trim()
        if (!id || seen.has(id)) return
        seen.add(id)
        rows.push({ id, name: opt.text || id })
      })
      ;[
        this.form.agentIdFresh,
        this.form.agentIdDrive,
        this.form.agentIdOther,
        this.form.agentId,
      ].forEach((raw) => {
        const id = String(raw || '').trim()
        if (!id || seen.has(id)) return
        seen.add(id)
        rows.push({ id, name: this.agentDisplayName(id) })
      })
      return rows
    },
  },
  watch: {
    activeTab(step) {
      if (step === 4 && this.canManage) {
        this.loadPromptTemplates()
      }
    },
    'form.transcriptLanguage'(lang) {
      if (!voiceMatchesLanguage(this.form.voice, lang)) {
        this.form.voice = defaultVoiceForLanguage(lang)
      }
    },
  },
  created() {
    if (this.canManage) {
      this.loadEmployees()
      this.loadConfig()
    }
  },
  methods: {
    goPrev() {
      if (this.activeTab > 0) this.activeTab -= 1
    },
    goNext() {
      if (this.activeTab < this.wizardSteps.length - 1) this.activeTab += 1
    },
    varToken(key) {
      return `{{${key}}}`
    },
    addCatalogInfield(listKey) {
      if (!Array.isArray(this.form[listKey])) this.$set(this.form, listKey, [])
      this.form[listKey].push({
        key: '',
        label: '',
        type: 'TEXT',
        required: false,
        visible: true,
      })
    },
    addCatalogOutcome(listKey) {
      if (!Array.isArray(this.form[listKey])) this.$set(this.form, listKey, [])
      this.form[listKey].push({
        outcome_name: '',
        outcome_description: '',
        outcome_type: 'TEXT',
        outcome_enum: null,
        is_visible: true,
      })
    },
    addCatalogLanguage() {
      if (!Array.isArray(this.form.languages)) this.$set(this.form, 'languages', [])
      this.form.languages.push({ value: '', text: '' })
    },
    addCatalogVoice() {
      if (!Array.isArray(this.form.voices)) this.$set(this.form, 'voices', [])
      this.form.voices.push({ id: '', language: 'en', name: '', gender: 'female' })
    },
    addPresetRow(group) {
      if (!this.form.presetPromptLines || typeof this.form.presetPromptLines !== 'object') {
        this.$set(this.form, 'presetPromptLines', { fresh: [], drive: [] })
      }
      if (!Array.isArray(this.form.presetPromptLines[group])) {
        this.$set(this.form.presetPromptLines, group, [])
      }
      this.form.presetPromptLines[group].push({
        id: '',
        label: '',
        brief: '',
        description: '',
      })
    },
    removePresetRow(group, index) {
      if (this.form.presetPromptLines && Array.isArray(this.form.presetPromptLines[group])) {
        this.form.presetPromptLines[group].splice(index, 1)
      }
    },
    removeCatalogRow(listKey, index) {
      if (Array.isArray(this.form[listKey])) this.form[listKey].splice(index, 1)
    },
    enumToText(value) {
      return Array.isArray(value) ? value.join(', ') : ''
    },
    setOutcomeEnum(item, raw) {
      const parts = String(raw || '').split(',').map(s => s.trim()).filter(Boolean)
      this.$set(item, 'outcome_enum', parts.length ? parts : null)
    },
    setTokenList(field, nested, raw) {
      const parts = String(raw || '').split(',').map(s => s.trim()).filter(Boolean)
      if (nested) {
        if (!this.form[field] || typeof this.form[field] !== 'object') {
          this.$set(this.form, field, { fresh: [], drive: [] })
        }
        this.$set(this.form[field], nested, parts)
        return
      }
      this.$set(this.form, field, parts)
    },
    humanVoiceLabel,
    agentVoiceLanguage(agentId) {
      return getAgentVoiceSettings(this.form.agentVoiceMap, agentId, {
        agentName: this.agentDisplayName(agentId),
        assignedScreeningId: this.form.agentIdFresh,
        assignedDriveId: this.form.agentIdDrive,
      }).language
    },
    agentVoiceCode(agentId) {
      return getAgentVoiceSettings(this.form.agentVoiceMap, agentId, {
        agentName: this.agentDisplayName(agentId),
        assignedScreeningId: this.form.agentIdFresh,
        assignedDriveId: this.form.agentIdDrive,
      }).voice
    },
    setAgentVoicePair(agentId, patch) {
      const id = String(agentId || '').trim()
      if (!id) return
      const next = normalizeAgentVoiceEntry({
        ...getAgentVoiceSettings(this.form.agentVoiceMap, id, {
          agentName: this.agentDisplayName(id),
        }),
        ...patch,
      }, { agentId: id, agentName: this.agentDisplayName(id) })
      this.$set(this.form.agentVoiceMap, id, next)
    },
    voiceSelectOptionsForAgent(agentId) {
      return voiceSelectOptionsForLanguage(this.agentVoiceLanguage(agentId))
    },
    onAgentVoiceLanguage(agentId, language) {
      const current = this.agentVoiceCode(agentId)
      this.setAgentVoicePair(agentId, {
        language,
        voice: voiceMatchesLanguage(current, language)
          ? current
          : defaultVoiceForLanguage(language),
      })
    },
    onAgentVoiceCode(agentId, voice) {
      this.setAgentVoicePair(agentId, { voice })
    },
    seedAgentVoiceMap() {
      this.voiceAgentRows.forEach((row) => {
        if (this.form.agentVoiceMap[row.id]) return
        this.$set(this.form.agentVoiceMap, row.id, defaultAgentVoicePair({
          agentId: row.id,
          agentName: row.name,
          assignedScreeningId: this.form.agentIdFresh,
          assignedDriveId: this.form.agentIdDrive,
        }))
      })
    },
    agentOptionById(agentId) {
      const id = (agentId || '').toString().trim()
      if (!id) return null
      return this.agentOptions.find(o => o.value === id) || null
    },
    agentDisplayName(agentId) {
      const id = (agentId || '').toString().trim()
      if (!id) return '—'
      const opt = this.agentOptionById(id)
      return (opt && opt.text) || id
    },
    agentDisplayCode(agentId) {
      const id = (agentId || '').toString().trim()
      if (!id) return ''
      const opt = this.agentOptionById(id)
      if (opt && opt.code && opt.code !== opt.text) return opt.code
      if (opt && opt.text && opt.text !== id) return id
      return ''
    },
    normalizeAgentOptions(raw) {
      const list = extractIndusAgentsList(raw)
      return list
        .map(a => {
          if (typeof a === 'string') return { value: a, text: a, code: '' }
          const id = a.agent_id || a.agentId || a.id || a.value || ''
          if (!id) return null
          const name = a.name || a.agent_name || a.title || ''
          return {
            value: String(id),
            text: name || String(id),
            code: name ? String(id) : '',
          }
        })
        .filter(Boolean)
    },
    ensureAgentInOptions(agentId) {
      const id = (agentId || '').toString().trim()
      if (!id || isDeletedIndusAgent(id)) return
      if (this.agentOptions.some(o => o.value === id)) return
      this.agentOptions.unshift({ value: id, text: id, code: 'Saved' })
    },
    onTypedAgent(field, option) {
      const id = (option && (option.value || option.label || option)) || ''
      if (!id || !field) return
      this.form[field] = String(id)
      this.ensureAgentInOptions(id)
    },
    onAgentPickerOpen() {
      if (this.agentsFetched || this.loadingAgents) return
      if (this.hasSavedAgentIds) return
      this.refreshAgents()
    },
    goToAgentsTab() {
      if (this.$route.name !== 'apps-admin-settings') return
      if (!this.canManage) return
      this.$router.replace({
        name: 'apps-admin-settings',
        query: { ...this.$route.query, tab: 'agents' },
      }).catch(() => {})
    },
    async loadPromptTemplates({ force = false } = {}) {
      if (this.templatesFetched && !force) return
      this.templatesFetched = true
      try {
        const res = await aiCallingService.listPromptTemplates()
        if (res && (res.status === 'OK' || Array.isArray(res.data))) {
          this.promptTemplates = Array.isArray(res.data) ? res.data : (res.templates || [])
        }
      } catch (e) {
        this.promptTemplates = []
      }
    },
    openTemplateForm(item = null) {
      if (item) {
        this.templateForm = {
          id: item.id,
          name: item.name || '',
          description: item.description || '',
          call_type: item.call_type || 'fresh',
          first_message: item.first_message || item.firstMessage || '',
          system_prompt: item.system_prompt || item.systemPrompt || '',
          active: item.active !== false,
        }
      } else {
        this.templateForm = {
          id: null,
          name: '',
          description: '',
          call_type: 'fresh',
          first_message: DEFAULT_FIRST_MESSAGE,
          system_prompt: DEFAULT_SYSTEM_PROMPT,
          active: true,
        }
      }
      this.templateFormOpen = true
    },
    async saveTemplate() {
      if (!(this.templateForm.name || '').trim()) {
        this.$bvToast.toast('Template name is required.', { title: 'Templates', variant: 'warning', solid: true })
        return
      }
      this.savingTemplate = true
      try {
        const payload = {
          name: this.templateForm.name.trim(),
          description: (this.templateForm.description || '').trim(),
          call_type: this.templateForm.call_type,
          first_message: this.templateForm.first_message,
          system_prompt: this.templateForm.system_prompt,
          active: this.templateForm.active !== false,
        }
        const res = this.templateForm.id
          ? await aiCallingService.updatePromptTemplate(this.templateForm.id, payload)
          : await aiCallingService.createPromptTemplate(payload)
        if (res && (res.status === 'OK' || res.status === 200 || res.data)) {
          this.templateFormOpen = false
          await this.loadPromptTemplates({ force: true })
          this.$bvToast.toast('Template saved.', { title: 'Templates', variant: 'success', solid: true })
        } else {
          this.$bvToast.toast((res && res.message) || 'Save failed.', { title: 'Templates', variant: 'danger', solid: true })
        }
      } finally {
        this.savingTemplate = false
      }
    },
    async removeTemplate(item) {
      if (!item || !item.id) return
      const ok = await this.$bvModal.msgBoxConfirm(
        `Remove template "${item.name}"? Recruiters will no longer see it.`,
        { title: 'Remove template', okVariant: 'danger', okTitle: 'Remove', cancelTitle: 'Cancel' },
      )
      if (!ok) return
      const res = await aiCallingService.deletePromptTemplate(item.id)
      if (res && (res.status === 'OK' || res.status === 200)) {
        await this.loadPromptTemplates({ force: true })
        this.$bvToast.toast('Template removed.', { title: 'Templates', variant: 'success', solid: true })
      }
    },
    async refreshAgents() {
      this.loadingAgents = true
      this.agentsLoadMsg = ''
      this.agentsLoadIsError = false
      try {
        this.agentsFetched = true
        const res = await aiCallingService.listIndusAgents()
        const parsed = parseIndusAgentsListResponse(res)
        if (parsed.ok) {
          this.agentOptions = this.normalizeAgentOptions(parsed.agents)
          this.ensureAgentInOptions(this.form.agentId)
          if (this.agentOptions.length) {
            this.agentsLoadMsg = `Loaded ${this.agentOptions.length} agent(s). Pick from the list, or type a name/id.`
          } else {
            this.agentsLoadMsg = 'No agents yet — create one in the Agents tab, or type a name/id.'
          }
        } else {
          this.agentOptions = []
          this.ensureAgentInOptions(this.form.agentId)
          this.ensureAgentInOptions(this.form.agentIdFresh)
          this.ensureAgentInOptions(this.form.agentIdDrive)
          this.ensureAgentInOptions(this.form.agentIdOther)
          this.agentsLoadIsError = true
          this.agentsLoadMsg = AGENTS_LIST_UNAVAILABLE_MSG
          // eslint-disable-next-line no-console
          console.error('[AiCallingSettings] agents list unavailable', parsed.error || res)
        }
      } catch (e) {
        this.agentOptions = []
        this.ensureAgentInOptions(this.form.agentId)
        this.ensureAgentInOptions(this.form.agentIdFresh)
        this.ensureAgentInOptions(this.form.agentIdDrive)
        this.ensureAgentInOptions(this.form.agentIdOther)
        this.agentsLoadIsError = true
        this.agentsLoadMsg = AGENTS_LIST_UNAVAILABLE_MSG
        // eslint-disable-next-line no-console
        console.error('[AiCallingSettings] agents list failed', e)
      } finally {
        this.seedAgentVoiceMap()
        this.loadingAgents = false
      }
    },
    async loadEmployees() {
      try {
        const res = await UserService.getAllUsersDdActiveInactive()
        if (!(res && res.status === 'OK' && Array.isArray(res.data))) {
          this.employeeOptions = []
          this.employeeById = {}
          return
        }
        const byId = {}
        const activeIds = new Set()
        res.data.forEach(u => {
          const id = u.emp_id || u.id
          if (!id) return
          const key = String(id)
          byId[key] = {
            value: key,
            text: String(employeeLabel(u) || id),
          }
          if (isActiveUser(u)) activeIds.add(key)
        })
        this.employeeById = byId
        this.employeeOptions = Object.values(byId)
          .filter(o => activeIds.has(o.value))
          .sort((a, b) => a.text.localeCompare(b.text))
        this.ensureSelectedEmployeesInOptions()
      } catch (e) {
        this.employeeOptions = []
        this.employeeById = {}
      }
    },
    /** Keep inactive / missing-from-active options so saved emp_ids still show Name. */
    ensureSelectedEmployeesInOptions() {
      const present = new Set(this.employeeOptions.map(o => o.value))
      ;(this.form.specialUsers || []).forEach(u => {
        const id = (u.emp_id || '').toString().trim()
        if (!id || present.has(id)) return
        const known = this.employeeById[id]
        this.employeeOptions.push(known || { value: id, text: id })
        present.add(id)
      })
    },
    employeeOptionsForRow(index) {
      const selectedElsewhere = new Set(
        (this.form.specialUsers || [])
          .map((u, i) => (i !== index ? String(u.emp_id || '').trim() : ''))
          .filter(Boolean),
      )
      const current = String((this.form.specialUsers[index] && this.form.specialUsers[index].emp_id) || '').trim()
      return this.employeeOptions.filter(o => !selectedElsewhere.has(o.value) || o.value === current)
    },
    normalizeConfig(raw) {
      const src = raw && typeof raw === 'object' ? raw : {}
      const data = src.data && typeof src.data === 'object' ? src.data : src
      const numbers = Array.isArray(data.numbers) ? data.numbers : []
      const systemPrompt = data.systemPrompt
        || data.defaultSystemPrompt
        || data.system_prompt
        || data.default_system_prompt
        || ''
      const firstMessage = data.firstMessage
        || data.defaultFirstMessage
        || data.first_message
        || data.default_first_message
        || ''
      const transcriptLanguage = normalizeTranscriptLanguage(
        data.transcriptLanguage || data.transcript_language,
      )
      const voice = canonicalizeIndusVoiceId(data.voice)
      rememberDeletedIndusAgents(data.deletedAgentIds || data.deleted_agent_ids || [])
      const dropDeleted = (id) => {
        const s = String(id || '').trim()
        return s && !isDeletedIndusAgent(s) ? s : ''
      }
      return {
        enabled: data.enabled !== false,
        number1: data.number1 || numbers[0] || '',
        number2: data.number2 || numbers[1] || '',
        maxConcurrent: Number(data.maxConcurrent) === 1 ? 1 : 2,
        voice,
        model: data.model || 'indus-tts-v1',
        outputFormat: data.outputFormat || data.output_format || 'mp3',
        speed: data.speed != null ? Number(data.speed) : 1,
        pitchShift: data.pitchShift != null ? Number(data.pitchShift) : (data.pitch_shift != null ? Number(data.pitch_shift) : 0),
        loudnessDb: data.loudnessDb != null ? Number(data.loudnessDb) : (data.loudness_db != null ? Number(data.loudness_db) : 0),
        sampleRate: data.sampleRate || data.sample_rate || 24000,
        agentId: dropDeleted(data.agentId || data.agent_id || ''),
        agentIdFresh: dropDeleted(data.agentIdFresh || data.agent_id_fresh || data.agentId || data.agent_id || ''),
        agentIdDrive: dropDeleted(data.agentIdDrive || data.agent_id_drive || ''),
        agentIdOther: dropDeleted(data.agentIdOther || data.agent_id_other || ''),
        transcriptLanguage,
        agentVoiceMap: normalizeAgentVoiceMap(data.agentVoiceMap || data.agent_voice_map || {}),
        systemPrompt: systemPrompt.trim() || '',
        firstMessage: firstMessage.trim() || '',
        defaultFreshScript: data.defaultFreshScript || data.default_fresh_script || '',
        defaultDriveScript: data.defaultDriveScript || data.default_drive_script
          || data.defaultFollowUpScript || data.default_follow_up_script || '',
        defaultFollowUpScript: data.defaultFollowUpScript || data.default_follow_up_script || '',
        specialUsers: Array.isArray(data.specialUsers)
          ? data.specialUsers.map(u => ({
            emp_id: u.emp_id || '',
            view: !!u.view,
            prompt: !!u.prompt,
            run: !!u.run,
          }))
          : [],
        ...resolveCatalogForEditor(catalogFallbacks()),
      }
    },
    async loadConfig() {
      this.loading = true
      this.loadError = ''
      this.statusMsg = ''
      try {
        const res = await adminConfigService.getAiCallingConfig()
        if (res && (res.status === 'OK' || res.status === 200 || res.data)) {
          applyAiCallingCatalog(res)
          this.form = this.normalizeConfig(res)
          this.ensureSelectedEmployeesInOptions()
          this.ensureAgentInOptions(this.form.agentId)
          this.ensureAgentInOptions(this.form.agentIdFresh)
          this.ensureAgentInOptions(this.form.agentIdDrive)
          this.ensureAgentInOptions(this.form.agentIdOther)
          if ((this.form.agentId || '').toString().trim()) this.showAdvanced = true
          this.statusMsg = 'Loaded'
        } else {
          this.form = DEFAULT_FORM()
          this.loadError = (res && (res.message || res.data?.message))
            || 'Could not load AI Calling config. You can still set defaults and save when the API is ready.'
        }
      } catch (e) {
        this.form = DEFAULT_FORM()
        this.loadError = 'Could not load AI Calling config yet.'
      } finally {
        this.loading = false
        if (this.canManage) {
          await this.refreshAgents()
          await this.ensureEnglishAgentsAssigned()
        }
      }
    },
    applyEnglishAgentAssignment(result) {
      if (!result) return false
      let changed = false
      const previousFresh = (this.form.agentIdFresh || this.form.agentId || '').toString().trim()
      if (result.screeningId && this.form.agentIdFresh !== result.screeningId) {
        if (previousFresh && previousFresh !== result.screeningId && !this.form.agentIdOther) {
          this.form.agentIdOther = previousFresh
        }
        this.form.agentIdFresh = result.screeningId
        this.form.agentId = result.screeningId
        changed = true
      }
      if (result.driveId && this.form.agentIdDrive !== result.driveId) {
        this.form.agentIdDrive = result.driveId
        changed = true
      }
      // Do not invent English scripts when assigning agents.
      this.ensureAgentInOptions(this.form.agentIdFresh)
      this.ensureAgentInOptions(this.form.agentIdDrive)
      this.ensureAgentInOptions(this.form.agentIdOther)
      return changed
    },
    async ensureEnglishAgentsAssigned() {
      if (this.englishAgentsEnsured) return
      this.englishAgentsEnsured = true
      try {
        const result = await aiCallingService.ensureEnglishOrgAgents()
        const changed = this.applyEnglishAgentAssignment(result)
        if (changed && this.form.number1 && this.form.number2) {
          await adminConfigService.saveAiCallingConfig(this.buildPayload())
        }
        if (result && result.created && result.created.length) {
          await this.refreshAgents()
        }
      } catch (e) {
        // Keep the flag so a failed lookup does not retry create on every load.
      }
    },
    addSpecialUser() {
      this.form.specialUsers.push({
        emp_id: '',
        view: true,
        prompt: false,
        run: false,
      })
    },
    removeSpecialUser(index) {
      this.form.specialUsers.splice(index, 1)
    },
    buildPayload() {
      return {
        enabled: !!this.form.enabled,
        number1: (this.form.number1 || '').trim(),
        number2: (this.form.number2 || '').trim(),
        numbers: [
          (this.form.number1 || '').trim(),
          (this.form.number2 || '').trim(),
        ].filter(Boolean),
        maxConcurrent: this.form.maxConcurrent === 1 ? 1 : 2,
        voice: this.form.voice,
        model: this.form.model,
        output_format: this.form.outputFormat,
        outputFormat: this.form.outputFormat,
        speed: this.form.speed,
        pitch_shift: this.form.pitchShift,
        pitchShift: this.form.pitchShift,
        loudness_db: this.form.loudnessDb,
        loudnessDb: this.form.loudnessDb,
        sample_rate: this.form.sampleRate,
        sampleRate: this.form.sampleRate,
        agent_id: this.form.agentIdFresh || this.form.agentId,
        agentId: this.form.agentIdFresh || this.form.agentId,
        agent_id_fresh: this.form.agentIdFresh || this.form.agentId,
        agentIdFresh: this.form.agentIdFresh || this.form.agentId,
        agent_id_drive: this.form.agentIdDrive,
        agentIdDrive: this.form.agentIdDrive,
        agent_id_other: this.form.agentIdOther,
        agentIdOther: this.form.agentIdOther,
        transcript_language: normalizeTranscriptLanguage(this.form.transcriptLanguage),
        transcriptLanguage: normalizeTranscriptLanguage(this.form.transcriptLanguage),
        agentVoiceMap: normalizeAgentVoiceMap(this.form.agentVoiceMap),
        agent_voice_map: normalizeAgentVoiceMap(this.form.agentVoiceMap),
        systemPrompt: this.form.systemPrompt,
        defaultSystemPrompt: this.form.systemPrompt,
        firstMessage: this.form.firstMessage,
        defaultFirstMessage: this.form.firstMessage,
        defaultFreshScript: this.form.defaultFreshScript,
        defaultDriveScript: this.form.defaultDriveScript,
        defaultFollowUpScript: this.form.defaultDriveScript || this.form.defaultFollowUpScript,
        specialUsers: this.form.specialUsers
          .filter(u => (u.emp_id || '').trim())
          .map(u => ({
            emp_id: u.emp_id.trim(),
            view: !!u.view,
            prompt: !!u.prompt,
            run: !!u.run,
          })),
        ...serializeAiCallingCatalog(this.form),
      }
    },
    async saveConfig() {
      if (!this.canManage) return
      if (!this.form.number1 || !this.form.number2) {
        this.$bvToast.toast('Please enter both outbound phone numbers (the numbers candidates will see).', {
          title: 'AI Calling',
          variant: 'warning',
          solid: true,
        })
        this.activeTab = 0
        return
      }
      if (!normalizeTranscriptLanguage(this.form.transcriptLanguage)
        || normalizeTranscriptLanguage(this.form.transcriptLanguage) === 'multi') {
        this.$bvToast.toast('Choose a default language before saving. Multi is a voice, not a language.', {
          title: 'AI Calling',
          variant: 'warning',
          solid: true,
        })
        this.activeTab = 1
        return
      }
      if (!String(this.form.voice || '').trim()) {
        this.$bvToast.toast('Choose a default voice before saving.', {
          title: 'AI Calling',
          variant: 'warning',
          solid: true,
        })
        this.activeTab = 1
        return
      }
      if (!(this.form.screeningInfields || []).length || !(this.form.screeningOutcomes || []).length) {
        this.$bvToast.toast('Configure screening infields and outcomes before saving. Empty schema is not allowed.', {
          title: 'AI Calling',
          variant: 'warning',
          solid: true,
        })
        return
      }
      this.saving = true
      this.statusMsg = ''
      try {
        const res = await adminConfigService.saveAiCallingConfig(this.buildPayload())
        if (res && (res.status === 'OK' || res.status === 200 || res.status === 'success')) {
          this.statusMsg = 'Saved'
          this.$bvToast.toast('AI Calling settings saved.', {
            title: 'Success',
            variant: 'success',
            solid: true,
          })
        } else {
          const msg = (res && (res.message || res.data?.message)) || 'Save failed — backend may not be ready yet.'
          this.statusMsg = msg
          this.$bvToast.toast(msg, {
            title: 'AI Calling',
            variant: 'danger',
            solid: true,
          })
        }
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.ai-calling-settings {
  --ai-brand: #00ac99;
  --ai-brand-soft: rgba(0, 172, 153, 0.12);
  --ai-brand-border: rgba(0, 172, 153, 0.4);
}
.ai-calling-settings__title {
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.3;
  color: #5e5873;
}
.ai-calling-settings__intro {
  max-width: 36rem;
}
.ai-calling-settings--embedded {
  margin-top: 0;
}
.ai-calling-section {
  border-color: #e5e3ea;
}
.ai-calling-section__title {
  font-size: 0.975rem;
  font-weight: 600;
  line-height: 1.3;
  color: #5e5873;
}
.ai-calling-settings ::v-deep .btn-primary {
  background-color: var(--ai-brand);
  border-color: var(--ai-brand);
}
.ai-calling-settings ::v-deep .btn-primary:hover,
.ai-calling-settings ::v-deep .btn-primary:focus {
  background-color: #009688;
  border-color: #009688;
}
.ai-calling-settings ::v-deep .btn-outline-primary {
  color: var(--ai-brand);
  border-color: var(--ai-brand);
}
.ai-calling-settings ::v-deep .btn-outline-primary:hover,
.ai-calling-settings ::v-deep .btn-outline-primary:focus {
  background-color: var(--ai-brand);
  border-color: var(--ai-brand);
  color: #fff;
}
.ai-calling-settings ::v-deep .ai-wizard-tabs .nav-link {
  padding: 0.35rem 0.85rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #6e6b7b;
}
.ai-calling-settings ::v-deep .ai-wizard-tabs .nav-link.active {
  background-color: var(--ai-brand);
  border-color: var(--ai-brand);
  color: #fff;
}
.ai-calling-settings ::v-deep .ai-calling-progress .progress-bar {
  background-color: var(--ai-brand);
}
.ai-calling-settings ::v-deep .ai-emp-select,
.ai-calling-settings ::v-deep .ai-agent-select {
  min-width: 160px;
  max-width: 100%;
}
.ai-calling-settings ::v-deep .ai-emp-select {
  max-width: 240px;
}
.ai-calling-settings ::v-deep .ai-agent-select .vs__dropdown-toggle {
  min-height: 38px;
  padding: 0 8px;
  border-color: #d8d6de;
}
.ai-calling-settings ::v-deep .ai-agent-select.vs--open .vs__dropdown-toggle,
.ai-calling-settings ::v-deep .ai-agent-select .vs__dropdown-toggle:hover {
  border-color: var(--ai-brand);
}
.ai-calling-settings ::v-deep .ai-emp-select .vs__dropdown-toggle {
  padding: 0 4px;
  min-height: 28px;
}
.ai-calling-settings ::v-deep .ai-emp-select .vs__search,
.ai-calling-settings ::v-deep .ai-emp-select .vs__selected,
.ai-calling-settings ::v-deep .ai-agent-select .vs__search,
.ai-calling-settings ::v-deep .ai-agent-select .vs__selected {
  font-size: 0.875rem;
  margin: 0;
  padding: 0 2px;
}
.ai-calling-settings ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
  background-color: var(--ai-brand);
  border-color: var(--ai-brand);
}
.ai-calling-link,
.ai-calling-settings ::v-deep .ai-calling-link {
  color: var(--ai-brand);
}
.ai-calling-brand-icon {
  color: var(--ai-brand);
}
.ai-calling-note {
  background: var(--ai-brand-soft);
  border: 1px solid var(--ai-brand-border);
  color: #5e5873;
}
.ai-calling-advanced {
  background: var(--ai-brand-soft);
  border: 1px solid var(--ai-brand-border);
}
.ai-calling-empty {
  border: 1px dashed var(--ai-brand-border);
  background: var(--ai-brand-soft);
  color: #5e5873;
}
.ai-calling-review {
  border-color: #ebe9f1;
}
.ai-calling-on-badge {
  background: var(--ai-brand-soft);
  color: #007a6e;
  border: 1px solid var(--ai-brand-border);
  font-size: 0.65rem;
  font-weight: 600;
  padding: 0.15rem 0.4rem;
}
.ai-calling-muted-id {
  font-size: 0.72rem;
  line-height: 1.3;
  letter-spacing: 0.01em;
  color: #b9b9c3;
}
.ai-prompt-textarea {
  font-size: 0.875rem;
  line-height: 1.45;
}
.ai-var-chip {
  font-family: Consolas, 'Courier New', monospace;
  font-weight: 500;
  font-size: 0.75rem;
}
.ai-preview-box {
  background: #fafafa;
  max-height: 280px;
  overflow: auto;
}
.ai-preview-pre {
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.45;
}
.ai-review-dl dt,
.ai-review-dl dd {
  margin: 0;
}
.ai-wizard-footer {
  position: sticky;
  bottom: 0;
  background: var(--white, #fff);
  z-index: 2;
}
</style>
